import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { Botao, Chip, Modal, Nota } from '../../../ui';
import { sincronizarSgaRegionais } from '../../../api/sga-regional/sga-regionalApi';
import { sincronizarSgaCooperativas } from '../../../api/sga-cooperativa/sga-cooperativaApi';
import { sincronizarSgaTipoVeiculos } from '../../../api/sga-tipo-veiculo/sga-tipo-veiculoApi';
import { extrairMensagemErro } from '../../../utils/apiError';

/**
 * Sincroniza os espelhos de REGIONAIS e COOPERATIVAS (at-34, item 7) e de
 * TIPOS DE VEÍCULO (at-44, bug B8) com o SGA.
 * Reescrito sobre o design system na at-67 (frente D/D5).
 *
 * Catálogos pequenos, sync **síncrono**: um botão roda os três em sequência (a
 * mesma ordem do cron) e mostra o desfecho de cada um. O cron diário
 * (6h/6h05/6h10) faz o mesmo sozinho — este modal só antecipa.
 *
 * 🔴 **Correção de comportamento da at-67: falha parcial deixou de ser anunciada
 * como sucesso.** A versão anterior mostrava um bloco verde escrito
 * *"Sincronização concluída"* assim que **qualquer** uma das três respondesse, e
 * a que falhou aparecia só num `toast.error` — que some sozinho. Dava para ler
 * "concluída" numa tela em que o espelho de tipos de veículo não tinha sido
 * atualizado, que é justamente o que faz um tipo novo do SGA não virar pendência
 * em Planos de Cotação. Agora **cada tabela mostra o próprio desfecho na tela**, e
 * o bloco só se chama "Resultado".
 */

interface ResultadoSync {
  sincronizadas: number;
  inativadas: number;
}

/** O desfecho de UMA das três tabelas. `null` = ainda não rodou. */
type Desfecho = { ok: true; resultado: ResultadoSync } | { ok: false; erro: string } | null;

interface ModalSincronizarRegionaisCooperativasProps {
  isOpen: boolean;
  onClose: () => void;
}

const ModalSincronizarRegionaisCooperativas: React.FC<
  ModalSincronizarRegionaisCooperativasProps
> = ({ isOpen, onClose }) => {
  const [sincronizando, setSincronizando] = useState(false);
  const [regionais, setRegionais] = useState<Desfecho>(null);
  const [cooperativas, setCooperativas] = useState<Desfecho>(null);
  const [tipos, setTipos] = useState<Desfecho>(null);

  // Reabrir o modal não deve mostrar o resultado da rodada anterior como se
  // fosse desta — o estado de sucesso/erro é de uma execução, não do catálogo.
  useEffect(() => {
    if (!isOpen) return;
    setRegionais(null);
    setCooperativas(null);
    setTipos(null);
  }, [isOpen]);

  const handleSincronizar = async () => {
    setSincronizando(true);
    setRegionais(null);
    setCooperativas(null);
    setTipos(null);

    // Em sequência (mesma ordem do cron): uma falha não impede a outra —
    // cada tabela mostra o próprio desfecho.
    const executar = async (
      acao: () => Promise<ResultadoSync>,
      registrar: (d: Desfecho) => void,
      rotulo: string,
    ): Promise<boolean> => {
      try {
        registrar({ ok: true, resultado: await acao() });
        return true;
      } catch (falha) {
        registrar({
          ok: false,
          erro: extrairMensagemErro(falha, `Não foi possível sincronizar ${rotulo}.`),
        });
        return false;
      }
    };

    const okRegionais = await executar(sincronizarSgaRegionais, setRegionais, 'as regionais');
    const okCooperativas = await executar(
      sincronizarSgaCooperativas,
      setCooperativas,
      'as cooperativas',
    );
    // Tipos de veículo entraram na at-44 (bug B8): o espelho não tinha cron nem
    // disparo de tela, então ficava congelado — e é ele que faz um tipo novo do
    // SGA virar pendência em Planos de Cotação.
    const okTipos = await executar(
      sincronizarSgaTipoVeiculos,
      setTipos,
      'os tipos de veículo',
    );

    if (okRegionais && okCooperativas && okTipos) {
      toast.success('Sincronização concluída.');
    }
    // ⚠️ Falha NÃO vira toast: ela já está na tela, por tabela, e some só quando
    // o TI fechar o modal. Um toast a mais seria a mesma informação duas vezes —
    // uma delas efêmera.

    setSincronizando(false);
  };

  const linha = (rotulo: string, desfecho: Desfecho) => {
    if (!desfecho) return null;

    return (
      <div key={rotulo} className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
        <span className="tw-text-sm tw-font-semibold tw-text-texto-titulo">{rotulo}</span>
        {desfecho.ok ? (
          <Chip variante="ok">
            {desfecho.resultado.sincronizadas} sincronizada(s) · {desfecho.resultado.inativadas}{' '}
            inativada(s)
          </Chip>
        ) : (
          <>
            <Chip variante="perigo">Falhou</Chip>
            <span className="tw-w-full tw-text-xs tw-text-danger">{desfecho.erro}</span>
          </>
        )}
      </div>
    );
  };

  const temResultado = !!(regionais || cooperativas || tipos);

  return (
    <Modal
      aberto={isOpen}
      titulo="Sincronizar Regionais, Cooperativas e Tipos (SGA)"
      ocupado={sincronizando}
      aoFechar={onClose}
      rodape={
        <Botao variante="secundario" onClick={onClose} disabled={sincronizando}>
          Fechar
        </Botao>
      }
    >
      <div className="tw-flex tw-flex-col tw-gap-4">
        {temResultado && (
          <div className="tw-flex tw-flex-col tw-gap-2 tw-rounded-xl tw-border tw-border-superficie-borda tw-bg-white tw-p-3">
            {/* ⚠️ "Resultado", nunca "Sincronização concluída" — ver o aviso no
                topo do arquivo. */}
            <p className="tw-m-0 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-texto-suave">
              Resultado
            </p>
            {linha('Regionais', regionais)}
            {linha('Cooperativas', cooperativas)}
            {linha('Tipos de veículo', tipos)}
          </div>
        )}

        <Botao
          variante="primario"
          larguraTotal
          carregando={sincronizando}
          onClick={handleSincronizar}
        >
          {sincronizando ? 'Sincronizando...' : 'Sincronizar agora'}
        </Botao>

        <Nota>
          Busca as listas atualizadas de regionais, cooperativas e tipos de veículo diretamente do
          SGA e atualiza os espelhos locais. Registro que deixou de existir lá é marcado INATIVO —
          nunca apagado. O sistema também faz isso sozinho todo dia (6h, 6h05 e 6h10). Um tipo de
          veículo novo aparece em <b>Planos de Cotação</b> como pendência até o plano ser cadastrado.
        </Nota>
      </div>
    </Modal>
  );
};

export default ModalSincronizarRegionaisCooperativas;
